import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaBell } from 'react-icons/fa';
import Header from '../components/layout/Header';
import NotificationDropdown from '../components/notifications/NotificationDropdown';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  position: relative;
`;

const Sidebar = styled.aside`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  position: fixed;
  height: 100vh;
  z-index: 999;
  transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease;
  width: 250px;
  padding: 2rem 0;
  
  @media (min-width: 992px) {
    position: fixed;
    transform: none;
    left: 0;
    top: 0;
  }
`;

const Content = styled.main`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
  overflow-y: auto;
  width: 100%;
  margin-left: 0;
  padding-top: 4rem;
  
  @media (min-width: 992px) {
    margin-left: 250px;
    width: calc(100% - 250px);
    padding-top: 1rem;
  }
`;

const ContentHeader = styled.div`
  display: none;
  
  @media (min-width: 992px) {
    display: block;
    padding-bottom: 1rem;
  }
`;

const ContentBody = styled.div`
  padding: 1rem;
`;

const Logo = styled.div`
  padding: 0 1.5rem;
  margin-bottom: 2rem;
  font-size: 1.25rem;
  font-weight: bold;
`;

const NavMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin-bottom: 0.5rem;
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.2s ease;
  background-color: ${({ $active }) => $active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
  
  svg {
    margin-right: 10px;
  }
`;

const MobileNavbar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  z-index: 998;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 992px) {
    display: none;
  }
`;

const MobileLogoText = styled.div`
  font-size: 1.25rem;
  font-weight: bold;
`;

const MobileMenuToggle = styled.button`
  background: none;
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
`;

const MobileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  
  @media (min-width: 992px) {
    display: none;
  }
`;

const MainContentWrapper = styled.div`
  width: 100%;
`;

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Close the sidebar when changing routes on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);
  
  const isPathActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };
  
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };
  
  return (
    <LayoutContainer>
      <MobileNavbar>
        <MobileMenuToggle onClick={toggleSidebar} aria-label="Toggle menu">
          {isSidebarOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuToggle>
        <MobileLogoText>Geo Diet Wonder</MobileLogoText>
        <MobileActions>
          <NotificationDropdown />
        </MobileActions>
      </MobileNavbar>
      
      <Overlay $isOpen={isSidebarOpen} onClick={closeSidebar} />
      
      <Sidebar $isOpen={isSidebarOpen}>
        <Logo>Geo Diet Wonder</Logo>
        <NavMenu>
          <NavItem>
            <NavLink to="/admin" $active={isPathActive('/admin') && location.pathname === '/admin'} onClick={closeSidebar}>
              Dashboard
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/partners" $active={isPathActive('/admin/partners')} onClick={closeSidebar}>
              Partners
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/customers" $active={isPathActive('/admin/customers')} onClick={closeSidebar}>
              Customers
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/plan-manage" $active={isPathActive('/admin/plan-manage')} onClick={closeSidebar}>
              Plan Management
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/content" $active={isPathActive('/admin/content')} onClick={closeSidebar}>
              Content Management
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/nutritionists" $active={isPathActive('/admin/nutritionists')} onClick={closeSidebar}>
              Wonder Kliniker
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/admin/notifications" $active={isPathActive('/admin/notifications')} onClick={closeSidebar}>
              <FaBell /> Notifications
            </NavLink>
          </NavItem>
        </NavMenu>
      </Sidebar>
      
      <MainContentWrapper>
        <Content>
          <ContentHeader>
            <Header />
          </ContentHeader>
          <ContentBody>
            {children}
          </ContentBody>
        </Content>
      </MainContentWrapper>
    </LayoutContainer>
  );
};

export default AdminLayout; 